import { useMemo } from 'react';
import EditPositionPreview from '../EditPositionPreview';
import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import useUserFee from '../../../../../hooks/web3/useUserFee';
import { calculatePnl } from '../../../../../helpers';
import useTokenStats from '../../../../../hooks/web3/useTokenStats';
import { toBigNumber } from '../../../../../web3/utils';
import { config } from '../../../../../config/config';

type SizePreviewProps = {
    userPosition: UserPositionRaw | undefined;
    amount: string;
    leverage: string;
};

const SizePreview: React.FC<SizePreviewProps> = ({ userPosition, amount, leverage }) => {
    const tokenStats = useTokenStats();
    const { userFee } = useUserFee();

    const pnlValues = userPosition
        ? calculatePnl(
              userPosition,
              tokenStats?.[userPosition.tradePair.chartSymbol]?.price
          )
        : undefined;

    // use toBigNumber here incase entryPrice is used which is in 24 decimals.
    const markPrice = useMemo(
        () =>
            tokenStats && userPosition && userPosition.tradePair
                ? toBigNumber(
                      tokenStats[userPosition.tradePair.chartSymbol]?.price ||
                          userPosition?.entryPrice ||
                          '0'
                  )
                : toBigNumber(userPosition?.entryPrice || '0'),
        [tokenStats, userPosition]
    );

    const addedCollateral = useMemo(
        () =>
            amount && leverage && userFee
                ? parseEther(amount)
                      .mul(parseEther('1'))
                      .div(
                          parseEther('1').add(
                              parseEther(leverage).mul(userFee).div(100_00)
                          )
                      )
                : parseEther('0'),
        [amount, leverage, userFee]
    );

    const addedSize = useMemo(
        () => addedCollateral.mul(parseEther(leverage || '0')).div(parseEther('1')),
        [addedCollateral, leverage]
    );

    const openFee = useMemo(
        () => addedSize.mul(userFee || 0).div(100_00),
        [addedSize, userFee]
    );

    const previewSize = useMemo(
        () => parseEther(userPosition?.volume || '0').add(addedSize),
        [userPosition?.volume, addedSize]
    );

    const previewCollateral = useMemo(
        () => parseEther(userPosition?.margin || '0').add(addedCollateral),
        [userPosition?.margin, addedCollateral]
    );

    const previewLeverage = useMemo(
        () => previewSize.mul(parseEther('1')).div(previewCollateral),
        [previewSize, previewCollateral]
    );

    const previewEntryPrice = useMemo(
        () =>
            tokenStats && userPosition && userPosition.tradePair && addedSize.gt(0)
                ? parseUnits(previewSize.toString(), config.priceFeedDecimal - 18)
                      .mul(parseUnits('1', config.priceFeedDecimal))
                      .div(
                          parseUnits(userPosition?.volume || '1', config.priceFeedDecimal)
                              .mul(parseUnits('1', config.priceFeedDecimal))
                              .div(
                                  parseUnits(
                                      userPosition?.entryPrice || '1',
                                      config.priceFeedDecimal
                                  )
                              )
                              .add(
                                  parseUnits(
                                      addedSize.toString(),
                                      config.priceFeedDecimal - 18
                                  )
                                      .mul(parseUnits('1', config.priceFeedDecimal))
                                      .div(
                                          parseUnits(
                                              tokenStats?.[
                                                  userPosition.tradePair?.chartSymbol
                                              ]?.price || '1',
                                              config.priceFeedDecimal
                                          )
                                      )
                              )
                      )
                : parseUnits(userPosition?.entryPrice || '1', config.priceFeedDecimal),
        [tokenStats, addedSize, previewSize, userPosition]
    );

    const previewBankruptcyPrice = useMemo(
        () =>
            previewEntryPrice
                .mul(
                    parseUnits('1', config.priceFeedDecimal)[
                        userPosition?.isShort ? 'add' : 'sub'
                    ](
                        parseUnits('1', config.priceFeedDecimal)
                            .mul(parseUnits('1', config.priceFeedDecimal))
                            .div(
                                parseUnits(
                                    previewLeverage.toString(),
                                    config.priceFeedDecimal - 18
                                )
                            )
                    )
                )
                .div(parseUnits('1', config.priceFeedDecimal)),
        [previewEntryPrice, previewLeverage, userPosition?.isShort]
    );
    return (
        <EditPositionPreview
            positionDetails={{
                tradePair: userPosition?.tradePair,
                bankruptcyPrice: {
                    value: userPosition?.bankruptcyPrice || '0',
                    previewValue: addedSize.gt(0)
                        ? formatUnits(previewBankruptcyPrice, config.priceFeedDecimal)
                        : undefined,
                },
                collateral: {
                    value: userPosition?.margin || '0',
                    previewValue: addedSize.gt(0)
                        ? formatEther(previewCollateral)
                        : undefined,
                },
                entryPrice: {
                    value: userPosition?.entryPrice || '0',
                    previewValue: addedSize.gt(0)
                        ? formatUnits(previewEntryPrice, config.priceFeedDecimal)
                        : undefined,
                },
                fees: {
                    value: undefined,
                    previewValue: addedSize.gt(0) ? formatEther(openFee) : undefined,
                },
                isShort: userPosition?.isShort ?? false, // should never happen, but just in case
                leverage: {
                    value: userPosition?.leverage || '0',
                    previewValue: addedSize.gt(0)
                        ? formatEther(previewLeverage)
                        : undefined,
                },
                markPrice: {
                    value: formatEther(markPrice) || '0',
                },
                pnl: {
                    value: pnlValues?.pnl,
                },
                size: {
                    value: userPosition?.volume || '0',
                    previewValue: addedSize.gt(0) ? formatEther(previewSize) : undefined,
                },
            }}
        />
    );
};

export default SizePreview;
